import { useEffect, useState } from 'react';
import { Copy, Loader2, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLicenseOrderAction } from './hooks/useLicenseOrders';
import {
  CHECKLIST_LABELS,
  PRIORITY_LABEL,
  WAITING_ON_LABEL,
  WORKFLOW_STAGE_LABEL,
} from './opsLabels';
import type {
  LicenseDuplicateCandidate,
  LicenseOrderDetail,
  LicenseOrderOperations,
  OpsPriority,
  WaitingOn,
  WorkflowStage,
} from './types';

const STAGES = Object.keys(WORKFLOW_STAGE_LABEL) as WorkflowStage[];
const WAITINGS = Object.keys(WAITING_ON_LABEL) as WaitingOn[];

interface Props {
  detail: LicenseOrderDetail;
  onUpdated: () => void;
}

export function LicenseOperationsSection({ detail, onUpdated }: Props) {
  const ops = detail.operations;
  const action = useLicenseOrderAction(detail.id);
  const [draft, setDraft] = useState<Partial<LicenseOrderOperations>>({});
  const [feeRef, setFeeRef] = useState('');
  const [submissionRef, setSubmissionRef] = useState('');
  const [duplicateOf, setDuplicateOf] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!ops) return;
    setDraft({
      workflowStage: ops.workflowStage,
      waitingOn: ops.waitingOn,
      nextAction: ops.nextAction,
      nextReviewAt: ops.nextReviewAt,
      priority: ops.priority,
      assignedTo: ops.assignedTo,
      operationalNotes: ops.operationalNotes,
      checklist: { ...ops.checklist },
      supportCaseReference: ops.supportCaseReference,
    });
    setFeeRef(ops.officialFeeReference || '');
    setSubmissionRef(ops.submissionReference || '');
    setDuplicateOf(ops.duplicateOfOrderId || '');
  }, [ops]);

  if (!ops) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
        Sin fila operativa todavía (recarga tras migrar).
      </div>
    );
  }

  async function run(body: Record<string, unknown>) {
    setMsg(null);
    try {
      await action.mutateAsync({ ...body, expectedVersion: ops!.version });
      onUpdated();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Error');
    }
  }

  async function saveHeader() {
    await run({
      action: 'update_operations',
      workflowStage: draft.workflowStage,
      waitingOn: draft.waitingOn,
      nextAction: draft.nextAction,
      nextReviewAt: draft.nextReviewAt || null,
      priority: draft.priority,
      assignedTo: draft.assignedTo,
      operationalNotes: draft.operationalNotes,
      supportCaseReference: draft.supportCaseReference,
      checklist: draft.checklist,
    });
  }

  const whatsappText =
    detail.whatsappSubmittedTemplate ||
    `Hola, tu pedido ${detail.publicId} está en trámite. App Android: https://play.google.com/store/apps/details?id=app.aipeces.main · iOS: https://apps.apple.com/es/app/aipeces/id6760563229 · Con la licencia definitiva recibirás cupón AIPECESPRO-3MESES.`;
  const phoneDigits = String(detail.contactPhone || '').replace(/\D/g, '');
  const waUrl = phoneDigits
    ? `https://wa.me/${phoneDigits.startsWith('34') ? phoneDigits : `34${phoneDigits}`}?text=${encodeURIComponent(whatsappText)}`
    : null;

  return (
    <div className="space-y-4 rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-sm">Operativa</h3>
          <p className="text-xs text-muted-foreground">
            v{ops.version} · actualizado {ops.updatedAt ? new Date(ops.updatedAt).toLocaleString('es-ES') : '—'}
            {ops.updatedBy ? ` · ${ops.updatedBy}` : ''}
          </p>
        </div>
        {ops.archivedAt ? (
          <span className="text-xs text-rose-500 font-medium">Archivado</span>
        ) : null}
      </div>

      {(detail.warnings || []).length > 0 ? (
        <ul className="space-y-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
          {detail.warnings!.map((w) => (
            <li key={w}>⚠ {w}</li>
          ))}
        </ul>
      ) : null}

      {(detail.duplicates || []).length > 0 ? (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-xs space-y-1">
          <div className="font-medium text-rose-400">Posibles duplicados</div>
          {(detail.duplicates as LicenseDuplicateCandidate[]).map((d) => (
            <div key={d.orderId}>
              {d.publicId} · {d.status}
              {d.submittedAt ? ' · presentada' : ''}
              {d.officialFeePaidAt ? ' · tasa pagada' : ''}
              {' · '}
              {d.matchReasons.join(', ')}
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Fase</span>
          <select
            value={draft.workflowStage || ops.workflowStage}
            onChange={(e) =>
              setDraft((p) => ({ ...p, workflowStage: e.target.value as WorkflowStage }))
            }
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          >
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {WORKFLOW_STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Esperando a</span>
          <select
            value={draft.waitingOn || ops.waitingOn}
            onChange={(e) => setDraft((p) => ({ ...p, waitingOn: e.target.value as WaitingOn }))}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          >
            {WAITINGS.map((s) => (
              <option key={s} value={s}>
                {WAITING_ON_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 col-span-2">
          <span className="text-xs text-muted-foreground">Siguiente acción</span>
          <input
            value={draft.nextAction ?? ''}
            onChange={(e) => setDraft((p) => ({ ...p, nextAction: e.target.value }))}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Próxima revisión</span>
          <input
            type="datetime-local"
            value={toLocalInput(draft.nextReviewAt || null)}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                nextReviewAt: e.target.value ? new Date(e.target.value).toISOString() : null,
              }))
            }
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Prioridad</span>
          <select
            value={draft.priority || ops.priority}
            onChange={(e) => setDraft((p) => ({ ...p, priority: e.target.value as OpsPriority }))}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          >
            <option value="normal">{PRIORITY_LABEL.normal}</option>
            <option value="urgent">{PRIORITY_LABEL.urgent}</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Responsable</span>
          <input
            value={draft.assignedTo ?? ''}
            onChange={(e) => setDraft((p) => ({ ...p, assignedTo: e.target.value }))}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Caso soporte</span>
          <input
            value={draft.supportCaseReference ?? ''}
            onChange={(e) => setDraft((p) => ({ ...p, supportCaseReference: e.target.value }))}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="space-y-1 col-span-2">
          <span className="text-xs text-muted-foreground">Notas operativas (privadas)</span>
          <textarea
            value={draft.operationalNotes ?? ''}
            onChange={(e) => setDraft((p) => ({ ...p, operationalNotes: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-border bg-background px-2 py-1.5"
          />
        </label>
      </div>

      <button
        type="button"
        disabled={action.isPending}
        onClick={() => void saveHeader()}
        className="px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50"
      >
        {action.isPending ? <Loader2 size={14} className="inline animate-spin" /> : null} Guardar cabecera
      </button>

      <div>
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Lista de comprobación</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {CHECKLIST_LABELS.map(({ key, label }) => {
            const checked = Boolean(draft.checklist?.[key]);
            return (
              <label key={key} className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      checklist: { ...(p.checklist || {}), [key]: e.target.checked },
                    }))
                  }
                  className="mt-0.5"
                />
                <span className={cn(checked && 'text-foreground', !checked && 'text-muted-foreground')}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <div className="flex items-center gap-1">
          <input
            placeholder="Ref. tasa oficial"
            value={feeRef}
            onChange={(e) => setFeeRef(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
          <button
            type="button"
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
            onClick={() => void run({ action: 'mark_fee_paid', officialFeeReference: feeRef })}
          >
            Tasa pagada
          </button>
        </div>
        <div className="flex items-center gap-1">
          <input
            placeholder="Nº registro"
            value={submissionRef}
            onChange={(e) => setSubmissionRef(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
          <button
            type="button"
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
            onClick={() =>
              void run({ action: 'mark_submitted', submissionReference: submissionRef })
            }
          >
            Presentada
          </button>
        </div>
        <button
          type="button"
          className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
          onClick={() => void run({ action: 'record_requirement' })}
        >
          Requerimiento
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
          onClick={() => void run({ action: 'mark_license_received' })}
        >
          Licencia recibida
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
          onClick={() => void run({ action: 'mark_client_informed' })}
        >
          Cliente informado
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
          onClick={() => void run({ action: 'mark_whatsapp_sent' })}
        >
          WhatsApp enviado
        </button>
        <div className="flex items-center gap-1">
          <input
            placeholder="UUID expediente principal"
            value={duplicateOf}
            onChange={(e) => setDuplicateOf(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 w-48"
          />
          <button
            type="button"
            className="px-2 py-1 rounded bg-rose-500/20 text-rose-400 hover:bg-rose-500/30"
            onClick={() => void run({ action: 'mark_duplicate', duplicateOfOrderId: duplicateOf })}
          >
            Marcar duplicado
          </button>
        </div>
        {ops.archivedAt ? (
          <button
            type="button"
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
            onClick={() => void run({ action: 'reopen' })}
          >
            Reabrir
          </button>
        ) : (
          <button
            type="button"
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80"
            onClick={() => void run({ action: 'archive' })}
          >
            Archivar
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted hover:bg-muted/80"
          onClick={() => void navigator.clipboard.writeText(whatsappText)}
        >
          <Copy size={12} /> Copiar WhatsApp (presentación)
        </button>
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
          >
            <MessageCircle size={12} /> Abrir WhatsApp
          </a>
        ) : null}
      </div>

      {detail.documents?.deliveryMode === 'postpay_pending' ? (
        <p className="text-xs text-amber-400">
          deliveryMode = postpay_pending (pago antes de fotos/firma).
        </p>
      ) : null}

      {ops.attachments?.length ? (
        <div className="text-xs text-muted-foreground">
          Adjuntos tipificados: {ops.attachments.map((a) => a.type).join(', ')}
        </div>
      ) : null}

      {msg ? <p className="text-sm text-rose-500">{msg}</p> : null}
    </div>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
